import { previewApplicationFile } from '../../utils/applicationFiles';
import UnreadChatBadge from '../UnreadChatBadge';
import ApplicationCabinetOwner from './ApplicationCabinetOwner';
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Sheet,
    Stack,
    Typography,
    IconButton,
    Chip,
    Button,
    Input,
    Select,
    Option,
    CircularProgress,
    Tooltip,
} from "@mui/joy";
import { motion, AnimatePresence } from 'framer-motion';
import { applicationAddress } from '../../utils/address';
import { APIEnsureApplicationChat, APIGetApplicationDetail } from '../../api';

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DataUsageRoundedIcon from '@mui/icons-material/DataUsageRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';

import { API_URL, APIDeleteClientApplication, APIDiscardDraft, APIHideClientApplication, APIMarkApplicationSeen, APIRetryApplicationDeal, APISetApplicationRevision } from '../../api';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import { LayoutContext } from "../../context";

import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Фикс для иконки маркера в webpack/vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});



export const STATUS_CONFIG = {
    draft: {
        label: 'Черновик',
        color: 'neutral',
        accent: '#64748b',
        bgSoft: 'rgba(100, 116, 139, 0.08)',
    },
    new: {
        label: 'Новая',
        color: 'primary',
        accent: '#572AC3',
        bgSoft: 'rgba(87, 42, 195, 0.06)',
    },
    measuring_premises: {
        label: 'Запланированные',
        color: 'warning',
        accent: '#f59e0b',
        bgSoft: 'rgba(245, 158, 11, 0.06)',
    },
    on_drawing: {
        label: 'На черчении',
        color: 'warning',
        accent: '#ca0bf5',
        bgSoft: 'rgba(245, 158, 11, 0.06)',
    },
    completed: {
        label: 'Завершена',
        color: 'success',
        accent: '#16a34a',
        bgSoft: 'rgba(22, 163, 74, 0.06)',
    },
    accepted: {
        label: 'Работа принята',
        color: 'primary',
        accent: '#0ea5e9',
        bgSoft: 'rgba(14, 165, 233, 0.08)',
    },
    revision: {
        label: 'Доработка',
        color: 'danger',
        accent: '#e11d48',
        bgSoft: 'rgba(225, 29, 72, 0.06)',
    },
    cancelled: {
        label: 'Отменена',
        color: 'danger',
        accent: '#dc2626',
        bgSoft: 'rgba(220, 38, 38, 0.06)',
    },
    hidden: {
        label: 'Скрытые',
        color: 'neutral',
        accent: '#9ca3af',
        bgSoft: 'rgba(107, 114, 128, 0.08)',
    },
};


function DetailRow({ label, value, multiline = false }) {
    return (
        <Sheet color="neutral" variant="soft" sx={{ p: 1, borderRadius: 'md' }}>
            <Typography
                level="body-xs"
                sx={{
                    color: 'text.tertiary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    fontWeight: 800,
                    // fontSize: '0.65rem',
                }}
            >
                {label}
            </Typography>
            <Typography
                level="body-sm"
                sx={{
                    mt: 0.25,
                    whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
                    overflow: multiline ? 'visible' : 'hidden',
                    textOverflow: multiline ? 'unset' : 'ellipsis',
                    color: 'text.primary',
                }}
            >
                {value}
            </Typography>
        </Sheet>
    );
}


export function buildFileUrl(path) {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path; // уже абсолютный
    return `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}


export function AddressMap({ address, lat, lng }) {
  const [marker, setMarker] = useState(
    lat && lng ? [lat, lng] : null
  );
  const [flyTarget, setFlyTarget] = useState(null);
  const geocodeTimer = useRef(null);

  // Обновить маркер при изменении lat/lng извне
  useEffect(() => {
    if (lat && lng) {
      setMarker([lat, lng]);
    }
  }, [lat, lng]);

  return (
    <Box
      sx={{
        borderRadius: "14px",
        overflow: "hidden",
        border: "1.5px solid",
        borderColor: "neutral.200",
        height: 260,
        mt: 1,
        "& .leaflet-container": {
          height: "100%",
          width: "100%",
          borderRadius: "12px",
        },
      }}
    >
      <MapContainer
        center={marker || [56.25, 43.45]}
        zoom={marker ? 16 : 5}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {marker && <Marker position={marker} />}
      </MapContainer>
    </Box>
  );
}

export function ApplicationDeleteModal({ open, application, onClose, onDeleteFull, onHide, onDiscard, isAdmin = false }) {
    if (!open || !application) return null;
    const isDraft = application.status === 'draft';

    return (
        <Box
            onClick={onClose}
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 10500,
                bgcolor: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Sheet
                onClick={(e) => e.stopPropagation()}
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    borderRadius: 'lg',
                    p: 2,
                    boxShadow: 'lg',
                }}
            >
                <Typography level="title-md">
                    {isDraft
                        ? `Удалить черновик #${application.id}?`
                        : (isAdmin ? `Удалить заявку #${application.id}?` : `Скрыть заявку #${application.id}?`)}
                </Typography>
                <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
                    {isDraft
                        ? 'Черновик будет удалён безвозвратно.'
                        : (isAdmin
                            ? 'Полное удаление уберёт заявку из базы. Скрытие просто уберёт её из активных списков.'
                            : 'Заявка будет скрыта из активных списков. Полностью удалять заявки может только главный администратор.')}
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    {isDraft ? (
                        <Button variant="solid" color="danger" onClick={onDiscard}>
                            Удалить черновик
                        </Button>
                    ) : (
                        <>
                            {isAdmin && (
                                <Button variant="solid" color="danger" onClick={onDeleteFull}>
                                    Удалить полностью
                                </Button>
                            )}
                            <Button variant="solid" color="warning" onClick={onHide}>
                                Скрыть
                            </Button>
                        </>
                    )}
                    <Button variant="solid" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                </Stack>
            </Sheet>
        </Box>
    );
}


// ════════════════════════════════════════════════════════
//  Карточка заявки
// ════════════════════════════════════════════════════════

export default function ApplicationCard(props) {
    const { tokenEmployee } = useContext(LayoutContext);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    useEffect(() => {
        let cancelled = false;
        setDetail(null); setError('');
        if (!props.expanded || !props.application?.id) return;
        APIGetApplicationDetail(props.application.id, tokenEmployee).then(data => {
            if (cancelled) return;
            if (data?.id) setDetail(data); else setError('Не удалось загрузить данные и файлы заявки.');
        }).catch(() => { if (!cancelled) setError('Не удалось загрузить данные и файлы заявки.'); });
        return () => { cancelled = true; };
    }, [props.expanded, props.application?.id, props.application?.date_update, tokenEmployee, retry]);
    return <Box>
        <ApplicationCardContent {...props} application={detail || props.application} />
        {props.expanded && error && <Stack direction="row" spacing={1} sx={{ p: 1 }}><Typography level="body-xs" color="danger">{error}</Typography><Button size="sm" onClick={() => setRetry(n => n + 1)}>Повторить</Button></Stack>}
    </Box>;
}

function ApplicationCardContent({ application, expanded, onToggleExpand, onCreateEvent, onEdit, onOpenEvent, onOpenClientChat, onOpenEventChat, onDeleted }) {
    const { account, tokenEmployee, notify, loadApplications, setChatModal } = useContext(LayoutContext);

    const [statusOverride, setStatusOverride] = useState(null);
    const [revLoading, setRevLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [seenLocally, setSeenLocally] = useState(false);
    const [chatFixing, setChatFixing] = useState(false);
    const [createdChat, setCreatedChat] = useState(null);
    const [teamOpening, setTeamOpening] = useState(false);
    useEffect(() => { setCreatedChat(null); }, [application.id]);
    const clientChatInfo = application.chat_info || createdChat
        || application.event?.client_chat
        || (application.chat_id ? { id: application.chat_id, name: `Заявка #${application.id}` } : null);
    const openClientChat = (info) => {
        if (info?.id) setChatModal({ open: true, chatId: info.id,
            chatName: info.name || `Заявка #${application.id}` });
    };

    // ── Сделка Битрикс заявки: локальный стейт, чтобы чип обновлялся сразу
    // после ручного создания сделки (без перезагрузки списка)
    const [dealId, setDealId] = useState(application.bitrix_deal || null);
    const [dealCreating, setDealCreating] = useState(false);
    const [dealError, setDealError] = useState('');
    useEffect(() => { setDealId(application.bitrix_deal || null); setDealError(''); }, [application.bitrix_deal, application.id]);

    const handleCreateDeal = async (e) => {
        e?.stopPropagation?.();
        if (dealCreating) return;
        setDealCreating(true);
        setDealError('');
        const res = await APIRetryApplicationDeal(application.id, tokenEmployee);
        setDealCreating(false);
        if (res?.status === 'ok' && res.bitrix_deal) {
            setDealId(res.bitrix_deal);
        } else {
            setDealError(res?.detail || 'Не удалось создать сделку');
        }
    };

    const isHidden = !!application.is_hidden;
    const effStatus = statusOverride || application.status;
    const cfg = isHidden ? STATUS_CONFIG.hidden : (STATUS_CONFIG[effStatus] || STATUS_CONFIG.new);
    const REVISIONABLE = ['measuring_premises', 'on_drawing', 'completed', 'revision'];
    const canRevision = !isHidden && REVISIONABLE.includes(effStatus);
    const handleToggleRevision = async (e) => {
        e.stopPropagation();
        const turnOn = effStatus !== 'revision';
        setRevLoading(true);
        const res = await APISetApplicationRevision(application.id, turnOn, '', tokenEmployee);
        setRevLoading(false);
        if (res?.status === 'ok') {
            setStatusOverride(res.new_status || (turnOn ? 'revision' : 'on_drawing'));
        }
    };
    const hasEvent = !!application.event;

    // Чип «Изменено» показываем, пока его не «погасили» локально
    const showUnseen = !!application.has_unseen_changes && !seenLocally;

    // При первом разворачивании карточки с непросмотренными изменениями —
    // гасим чип и сообщаем серверу (один раз).
    useEffect(() => {
        if (expanded && application.has_unseen_changes && !seenLocally) {
            setSeenLocally(true); // оптимистично убираем чип
            APIMarkApplicationSeen(application.id, tokenEmployee)
                .catch(() => setSeenLocally(false)); // при ошибке вернём чип
        }
    }, [expanded, application.has_unseen_changes, application.id, tokenEmployee, seenLocally]);

    const handleDelete = (e) => {
        e.stopPropagation();
        setDeleteModalOpen(true);
    };

    const handleDeleteFull = async () => {
        try {
            await APIDeleteClientApplication(application.id, tokenEmployee);
            setDeleteModalOpen(false);
            if (typeof onDeleted === 'function') onDeleted(application.id);
        } catch (err) {
            console.error('Ошибка удаления заявки:', err);
            alert(err?.response?.data?.error || 'Не удалось удалить заявку');
        }
    };

    const handleHide = async () => {
        try {
            await APIHideClientApplication(application.id, tokenEmployee);
            setDeleteModalOpen(false);
            if (typeof onDeleted === 'function') onDeleted(application.id);
        } catch (err) {
            console.error('Ошибка скрытия заявки:', err);
            alert(err?.response?.data?.error || 'Не удалось скрыть заявку');
        }
    };

    const handleDiscard = async () => {
        try {
            await APIDiscardDraft(application.id, tokenEmployee);
            setDeleteModalOpen(false);
            if (typeof onDeleted === 'function') onDeleted(application.id);
        } catch (err) {
            console.error('Ошибка удаления черновика:', err);
            alert(err?.response?.data?.detail || 'Не удалось удалить черновик');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
        >
            <Sheet
                sx={{
                    position: 'relative',
                    borderRadius: 'lg',
                    
                    bgcolor: isHidden ? 'neutral.100' : 'background.surface',
                    border: '1px solid',
                    borderColor: showUnseen 
                        ? '#f59e0b'  // оранжевая рамка при изменении
                        : (isHidden ? 'neutral.300' : 'divider'),
                    boxShadow: showUnseen ? '0 0 0 2px rgba(245,158,11,0.15)' : 'none',
                    overflow: 'hidden',
                    opacity: isHidden ? 0.62 : 1,
                    transition: 'all 0.2s ease',
                    filter: isHidden ? 'grayscale(1)' : 'none',
                    cursor: isHidden ? 'not-allowed' : 'pointer',
                    '&:hover': isHidden ? {} : {
                        borderColor: cfg.accent,
                        boxShadow: `0 4px 12px ${cfg.bgSoft}`,
                        transform: 'translateY(-1px)',
                    },
                }}
                onClick={isHidden ? undefined : onToggleExpand}
            >
                {/* Цветная полоска статуса слева */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        bgcolor: cfg.accent,
                    }}
                />

                <Box sx={{ p: 1.5, pl: 2 }}>
                    <ApplicationCabinetOwner application={application} />
                    {/* Шапка карточки */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', mb: 0.5, gap: 0.75 }}>
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        fontWeight: 700,
                                        color: cfg.accent,
                                        bgcolor: cfg.bgSoft,
                                        px: 0.75,
                                        py: 0.125,
                                        borderRadius: 'sm',
                                    }}
                                >
                                    #{application.id}
                                </Typography>
                                <Chip
                                    size="sm"
                                    color={cfg.color}
                                    variant="soft"
                                    startDecorator={<DataUsageRoundedIcon sx={{ fontSize: 12 }} />}
                                    sx={{ fontSize: '0.7rem', minHeight: 20 }}
                                >
                                    {cfg.label}
                                </Chip>
                                {isHidden && (
                                    <Chip size="sm" color="neutral" variant="soft" sx={{ fontSize: '0.7rem', minHeight: 20 }}>
                                        Скрыта
                                    </Chip>
                                )}

                                {/* ── Чип сделки Битрикс: есть/нет + создание в один клик ── */}
                                {dealId ? (
                                    <Tooltip title="Сделка Битрикс привязана" variant="soft">
                                        <Chip size="sm" color="success" variant="soft"
                                            sx={{ fontSize: '0.7rem', minHeight: 20 }}
                                            startDecorator={<span>🔗</span>}>
                                            Сделка №{dealId}
                                        </Chip>
                                    </Tooltip>
                                ) : application.status !== 'draft' && (
                                    <Tooltip
                                        title={dealError || application.pipeline_error
                                            || 'У заявки нет сделки Битрикс — нажмите, чтобы создать'}
                                        variant="soft"
                                    >
                                        <Chip
                                            size="sm"
                                            color="danger"
                                            variant="solid"
                                            onClick={handleCreateDeal}
                                            startDecorator={dealCreating
                                                ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />
                                                : <span>⚠️</span>}
                                            sx={{ fontSize: '0.7rem', minHeight: 20, cursor: 'pointer' }}
                                        >
                                            {dealCreating ? 'Создаём…' : 'Нет сделки — создать'}
                                        </Chip>
                                    </Tooltip>
                                )}

                                {Number(application.estimated_price || 0) > 0 && (
                                    <Chip size="sm" color="primary" variant="soft" sx={{ fontSize: '0.7rem', minHeight: 20 }}>
                                        {Number(application.estimated_price).toLocaleString('ru-RU')} ₽
                                        {application.estimated_price_is_manual ? ' (вручную)' : ''}
                                    </Chip>
                                )}

                                {showUnseen && (
                                    <Tooltip title="Клиент внёс изменения в заявку" variant="soft">
                                        <Chip size="sm" color="warning" variant="solid"
                                            sx={{ fontSize: '0.7rem', minHeight: 20, animation: 'pulse 1.5s ease-in-out infinite' }}
                                            startDecorator={<span>✏️</span>}>
                                            Изменено
                                        </Chip>
                                    </Tooltip>
                                )}

                                {application.referral_code_used && (
                                    <Tooltip title={`Код: ${application.referral_code_used} · Скидка: ${application.referral_discount_amount} ₽`} variant="soft">
                                        <Chip size="sm" color="primary" variant="soft" sx={{ fontSize: '0.7rem', minHeight: 20 }}>
                                            🎁 Реф. {Number(application.referral_discount_amount).toFixed(0)} ₽
                                        </Chip>
                                    </Tooltip>
                                )}

                                {hasEvent && (
                                    <Tooltip title={`Привязано к событию #${application?.event?.bitrix_deal?.id ?? application?.event?.deal_id ?? application?.event_id ?? ''}`} variant="soft">
                                        <Chip
                                            size="sm"
                                            color="success"
                                            variant="soft"
                                            startDecorator={<EventAvailableRoundedIcon sx={{ fontSize: 12 }} />}
                                            sx={{ fontSize: '0.7rem', minHeight: 20 }}
                                        >
                                            Событие #{application?.event?.bitrix_deal?.id}
                                        </Chip>
                                    </Tooltip>
                                )}
                            </Stack>
                            
                            <Stack direction={"column"} sx={{ gap: 0.5, alignItems: 'flex-start' }}>
                                <Typography
                                    level="title-sm"
                                    sx={{
                                        fontWeight: 600,
                                        lineHeight: 1.25,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {application.full_name || 'Без имени'}
                                </Typography>
                                { application?.client_info?.company_name && (
                                    <Chip component={'span'} size="sm" color="primary">
                                        {application?.client_info?.company_name}
                                    </Chip>
                                )}
                            </Stack>

                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ mt: 0.25, color: 'text.tertiary' }}
                            >
                                <PhoneRoundedIcon sx={{ fontSize: 12 }} />
                                
                                <Stack direction={{ xs: "column", sm: "row" }} sx={{ gap: 1, alignItems: 'center' }}>
                                    <Typography level="body-xs">
                                        {application.phone}
                                    </Typography>
                                </Stack>
                            </Stack>
                            {(application.city_name || application.address) && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    sx={{ mt: 0.25, color: 'text.tertiary' }}
                                >
                                    <LocationOnRoundedIcon sx={{ fontSize: 12, flexShrink: 0 }} />
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {applicationAddress(application)}
                                    </Typography>
                                </Stack>
                            )}
                        </Box>
                        <motion.div
                            animate={{ rotate: expanded ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ExpandMoreRoundedIcon sx={{ color: 'text.tertiary' }} />
                        </motion.div>
                    </Stack>

                    {/* Развёрнутая часть */}
                    <AnimatePresence initial={false}>
                        {expanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        pt: 1.5,
                                        borderTop: '1px dashed',
                                        borderColor: 'divider',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Stack spacing={1}>
                                        {application.address && application.latitude && application.longitude && (
                                            <AddressMap
                                                address={application.address}
                                                lat={application.latitude}
                                                lng={application.longitude}
                                            />
                                        )}
                                        {application.square != null && (
                                            <DetailRow label="Площадь" value={`${application.square} м²`} />
                                        )}
                                        {application.object_type && (
                                            <DetailRow label="Тип объекта" value={application.object_type} />
                                        )}
                                        {application.program_display && (
                                            <DetailRow label="Программа" value={application.program_display.name} />
                                        )}
                                        {application.tz && (
                                            <DetailRow label="ТЗ" value={application.tz} multiline />
                                        )}
                                        {(application.contact_name || application.contact_phone) && (
                                            <DetailRow
                                                label="Контакт на объекте"
                                                value={`${application.contact_name || '—'} ${application.contact_phone || ''}`.trim()}
                                            />
                                        )}
                                        {(application.desired_time_display || (application.desired_dates && application.desired_dates.length > 0) || application.desired_date || application.preferred_time_from || application.preferred_time_to) && (
                                            <Sheet color="neutral" variant="soft" sx={{ p: 1, borderRadius: 'md' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 800, mb: 0.5 }}>
                                                    Когда нужна услуга
                                                </Typography>

                                                {/* Вариант «выберу дни в календаре» — показываем сами даты */}
                                                {application.desired_time === 'pick_dates' && application.desired_slots && application.desired_slots.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {application.desired_slots.map(s => {
                                                            const dd = (s.date || '').split('-').reverse().join('.');
                                                            const tf = (s.time_from || '').slice(0, 5), tt = (s.time_to || '').slice(0, 5);
                                                            const label = tf && tt ? `${dd} ${tf}–${tt}` : (tf ? `${dd} с ${tf}` : dd);
                                                            return (
                                                                <Chip key={s.date} size="sm" variant="soft" color="primary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{label}</Chip>
                                                            );
                                                        })}
                                                    </Box>
                                                ) : application.desired_time === 'pick_dates' && application.desired_dates && application.desired_dates.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {application.desired_dates.map(d => (
                                                            <Chip key={d} size="sm" variant="soft" color="primary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                                                {d.split('-').reverse().join('.')}
                                                            </Chip>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <>
                                                        {application.desired_time_display && (
                                                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{application.desired_time_display}</Typography>
                                                        )}
                                                        {application.desired_date && (
                                                            <Typography level="body-sm">Желаемая дата: {formatDate(application.desired_date)}</Typography>
                                                        )}
                                                    </>
                                                )}

                                                {/* Предпочтительное время — общий хвост блока (прячем, если у дат своё время) */}
                                                {!(application.desired_slots && application.desired_slots.length > 0) && (application.preferred_time_from || application.preferred_time_to) && (
                                                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                                                        Время: {application.preferred_time_from?.slice(0, 5) || '—'} — {application.preferred_time_to?.slice(0, 5) || '—'}
                                                    </Typography>
                                                )}
                                            </Sheet>
                                        )}
                                        {application.change_logs && application.change_logs.length > 0 && (
                                            <Box sx={{ mt: 1.5 }}>
                                                <Typography level="body-xs" sx={{
                                                    color: 'text.tertiary', textTransform: 'uppercase',
                                                    letterSpacing: '0.03em', fontWeight: 800, mb: 0.75,
                                                }}>
                                                    История изменений
                                                </Typography>
                                                <Stack spacing={0.5}>
                                                    {application.change_logs.slice(0, 10).map((log, idx) => {
                                                        // source: 'employee' | 'client' | 'system'. by — объект {type,id,name}
                                                        // в менеджерской выдаче, либо строка в клиентской. Поддерживаем оба.
                                                        const src = log.source || log.by?.type || 'system';
                                                        const who = (log.by && typeof log.by === 'object') ? (log.by.name || '') : (log.by || '');
                                                        const tone = src === 'employee' ? 'primary' : src === 'client' ? 'warning' : 'neutral';
                                                        const srcLabel = src === 'employee' ? 'сотрудник' : src === 'client' ? 'клиент' : 'система';
                                                        return (
                                                            <Box key={idx} sx={{
                                                                p: 1, borderRadius: 'md',
                                                                bgcolor: `${tone}.50`,
                                                                border: '1px solid', borderColor: `${tone}.200`,
                                                            }}>
                                                                <Typography level="body-xs" sx={{ fontWeight: 700, color: `${tone}.700` }}>
                                                                    {log.label}: {log.old || '—'} → {log.new || '—'}
                                                                </Typography>
                                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                                    {who ? `${who} · ` : ''}{srcLabel} · {new Date(log.date).toLocaleString('ru-RU')}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>
                                        )}
                                        {application.contact_comment && (
                                            <DetailRow label="Комм. контакта" value={application.contact_comment} multiline />
                                        )}
                                        {application.referral_code_used && (
                                            <DetailRow
                                                label="Реферальный код"
                                                value={`${application.referral_code_used} · скидка ${application.referral_discount_amount} ₽`}
                                            />
                                        )}
                                        {application.comment && (
                                            <DetailRow label="Комментарий" value={application.comment} multiline />
                                        )}
                                        {application.plan_file && (
                                            <Box>
                                                <Typography
                                                    level="body-xs"
                                                    sx={{
                                                        color: 'text.tertiary',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.03em',
                                                        fontWeight: 600,
                                                        fontSize: '0.65rem',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    План помещения
                                                </Typography>
                                                <Button
                                                    size="sm"
                                                    variant="soft"
                                                    color="primary"
                                                    fullWidth
                                                    startDecorator={<FileDownloadRoundedIcon sx={{ fontSize: 16 }} />}
                                                    onClick={(e) => { e.stopPropagation(); previewApplicationFile({ link: application.plan_file, name: 'План помещения' }).catch(error => notify(error.message, 'error')); }}
                                                    sx={{
                                                        m: 1,
                                                        width: "stretch",
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        borderRadius: 'lg',
                                                        justifyContent: 'center',
                                                        background: 'linear-gradient(135deg, rgba(87, 42, 195, 0.08) 0%, rgba(141, 106, 236, 0.12) 100%)',
                                                        border: '1px solid',
                                                        borderColor: 'primary.200',
                                                        color: 'primary.600',
                                                        boxShadow: '0 1px 3px rgba(87, 42, 195, 0.08)',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, rgba(87, 42, 195, 0.14) 0%, rgba(141, 106, 236, 0.2) 100%)',
                                                            borderColor: 'primary.400',
                                                            boxShadow: '0 4px 12px rgba(87, 42, 195, 0.18)',
                                                        },
                                                    }}
                                                >
                                                    Открыть план
                                                </Button>
                                            </Box>
                                        )}

                                        {/* Файлы вводных данных от клиента */}
                                        {application.input_files && application.input_files.length > 0 && (
                                            <Box>
                                                <Typography
                                                    level="body-xs"
                                                    sx={{
                                                        color: 'text.tertiary',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.03em',
                                                        fontWeight: 800,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    Файлы от клиента ({application.input_files.length})
                                                </Typography>
                                                <Stack spacing={0.5}>
                                                    {application.input_files.map((file) => (
                                                        <Button
                                                            key={file.id}
                                                            size="sm"
                                                            variant="soft"
                                                            color="neutral"
                                                            startDecorator={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
                                                            endDecorator={
                                                                file.size ? (
                                                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                                        {file.size < 1024 * 1024
                                                                            ? `${(file.size / 1024).toFixed(0)} КБ`
                                                                            : `${(file.size / (1024 * 1024)).toFixed(1)} МБ`}
                                                                    </Typography>
                                                                ) : null
                                                            }
                                                            onClick={(e) => { e.stopPropagation(); previewApplicationFile(file).catch(error => notify(error.message, 'error')); }}
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                borderRadius: 'lg',
                                                                justifyContent: 'flex-start',
                                                                textAlign: 'left',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <Typography
                                                                level="body-sm"
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {file.original_name || `Файл #${file.id}`}
                                                            </Typography>
                                                        </Button>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}

                                        {application.date_create && (
                                            <DetailRow label="Дата создания заяв." value={new Date(application.date_create).toLocaleString('ru-RU', {
                                                day: '2-digit', month: '2-digit', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            })} />
                                        )}
                                        {application.created_by_info?.full_name && (
                                            <DetailRow label="Создал (сотрудник)" value={application.created_by_info.full_name} />
                                        )}
                                        {application.last_updated_by_info?.full_name && (
                                            <DetailRow label="Последним правил (сотрудник)" value={application.last_updated_by_info.full_name} />
                                        )}
                                        {application.calculator_amount && (
                                            <DetailRow label="Ожидание клиента (калькулятор сайта)" value={`${application.calculator_amount} ₽`} />
                                        )}
                                        {Number(application.estimated_price || 0) > 0 && (
                                            <DetailRow
                                                label={application.estimated_price_is_manual ? "Стоимость (задана вручную)" : "Стоимость (наш расчёт)"}
                                                value={`${Number(application.estimated_price).toLocaleString('ru-RU')} ₽`}
                                            />
                                        )}
                                        {Number(application.estimated_surveyor_salary || 0) > 0 && (
                                            <DetailRow label="ЗП обмерщика (расчётная)" value={`${Number(application.estimated_surveyor_salary).toLocaleString('ru-RU')} ₽`} />
                                        )}
                                        {Number(application.transfer_km || 0) > 0 && (
                                            <DetailRow label="Километраж выезда" value={`${application.transfer_km} км`} />
                                        )}
                                        {(application.services || []).length > 0 && (
                                            <DetailRow
                                                label="Услуги"
                                                value={(application.services_display || application.services || [])
                                                    .map((s) => (typeof s === 'object' ? s.title : s)).join(', ')}
                                            />
                                        )}
                                        {(application.addon_items || []).length > 0 && (
                                            <DetailRow
                                                label="Доп. услуги"
                                                value={(application.addon_items || []).map((a) => a.title).join(', ')}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Действия */}
                    <Stack
                        direction="row"
                        sx={{ mt: 1.5, gap: 0.75, flexWrap: 'wrap' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {hasEvent ? (
                            <Button
                                size="sm"
                                variant="solid"
                                color="success"
                                startDecorator={<EventAvailableRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={onOpenEvent}
                                disabled={isHidden}
                                sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 600 }}
                            >
                                №{application.event?.bitrix_deal?.id}
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                variant="solid"
                                color="primary"
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={application.status === 'draft' ? onEdit : onCreateEvent}
                                disabled={isHidden}
                                sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 600 }}
                            >
                                {application.status === 'draft' ? 'Оформить заявку' : 'Событие'}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="soft"
                            color="neutral"
                            onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
                            disabled={isHidden || hasEvent || !['new', 'draft'].includes(application.status)}
                            title="Редактировать заявку"
                            startDecorator={<EditRoundedIcon sx={{ fontSize: 16 }} />}
                        >
                            Изменить
                        </Button>
                        <Button
                            size="sm"
                            variant="soft"
                            color="danger"
                            onClick={handleDelete}
                            disabled={isHidden}
                            title="Удалить заявку"
                            startDecorator={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                        >
                            Удалить
                        </Button>
                        {canRevision && (
                            <Button
                                size="sm"
                                variant="soft"
                                color={effStatus === 'revision' ? 'success' : 'warning'}
                                onClick={handleToggleRevision}
                                loading={revLoading}
                                title="Переключить статус «Доработка»"
                                startDecorator={<BuildRoundedIcon sx={{ fontSize: 16 }} />}
                            >
                                {effStatus === 'revision' ? 'Снять доработку' : 'Доработка'}
                            </Button>
                        )}
                    </Stack>

                    <Stack
                        direction="row"
                        sx={{ mt: 1.5, gap: 0.5 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {clientChatInfo?.id ? (
                            <Button
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                startDecorator={<UnreadChatBadge chatId={clientChatInfo?.id}><ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>}
                                onClick={() => openClientChat(clientChatInfo)}
                                disabled={isHidden || chatFixing}
                                sx={{ fontSize: '0.75rem', width: '100%', fontWeight: 600 }}
                            >
                                Чат с клиентом
                            </Button>
                        ) : (
                            // Раньше кнопка просто была серой, и починить это мог только
                            // разработчик. Теперь менеджер создаёт чат сам.
                            <Button
                                startDecorator={<ChatRoundedIcon />}
                                variant="soft"
                                color="warning"
                                size="sm"
                                loading={chatFixing}
                                disabled={isHidden || application.status === 'draft'}
                                onClick={async () => {
                                    if (chatFixing) return;
                                    setChatFixing(true);
                                    try {
                                        const res = await APIEnsureApplicationChat(application.id, tokenEmployee);
                                        const info = res?.chat_info;
                                        if (res?.status !== 'ok' || !info?.id) {
                                            notify?.(res?.detail || 'Не удалось открыть чат клиента', 'danger');
                                            return;
                                        }
                                        setCreatedChat(info);
                                        openClientChat(info);
                                        loadApplications?.();
                                    } catch {
                                        notify?.('Не удалось открыть чат клиента', 'danger');
                                    } finally { setChatFixing(false); }
                                }}
                            >
                                Создать чат с кл.
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="soft"
                            color="primary"
                            startDecorator={<UnreadChatBadge chatId={application.event_chat_info?.id || application.event?.event_chat?.id}><ForumRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>}
                            loading={teamOpening}
                            onClick={async () => {
                                if (teamOpening) return;
                                setTeamOpening(true);
                                try { await onOpenEventChat?.(); }
                                finally { setTeamOpening(false); }
                            }}
                            disabled={isHidden || !hasEvent}
                            sx={{ fontSize: '0.75rem', width: '100%', fontWeight: 600 }}
                        >
                            Чат команды
                        </Button>
                    </Stack>
                </Box>
            </Sheet>

            <ApplicationDeleteModal
                open={deleteModalOpen}
                application={application}
                onClose={() => setDeleteModalOpen(false)}
                onDeleteFull={handleDeleteFull}
                onHide={handleHide}
                onDiscard={handleDiscard}
                isAdmin={account?.role === 'administrator'}
            />
        </motion.div>
    );
}